// src/components/admin/CustomerManagement.js
import { useState } from 'react';
import { collection, doc, addDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { DEFAULT_PRICING_TIERS, ORDER_METHODS, PAYMENT_METHODS } from '../../config/constants';
import { Building2, Plus, Edit2, Search, Loader2, ChevronDown, ChevronUp, MapPin, Link, Copy, Check, Trash2 } from 'lucide-react';

export function CustomerManagement({ customers, reps, loading, currentUserRole, currentUserId }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRep, setFilterRep] = useState('all');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedLink, setCopiedLink] = useState(null);

  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    repId: currentUserRole === 'rep' ? currentUserId : '',
    orderMethod: 'portal',
    paymentMethod: 'customerCC',
    pricingTier: 'standard',
    customDiscount: '',
    notes: '',
    // For portal access
    createPortalAccount: true,
    portalPassword: '',
    // Shipping locations
    shippingLocations: [{
      name: 'Primary',
      street: '',
      city: '',
      state: '',
      zip: '',
      isDefault: true
    }]
  });

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch =
      customer.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRep = filterRep === 'all' || 
      (filterRep === 'direct' && !customer.repId) ||
      customer.repId === filterRep;

    return matchesSearch && matchesRep;
  });

  const resetForm = () => {
    setFormData({
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      website: '',
      repId: currentUserRole === 'rep' ? currentUserId : '',
      orderMethod: 'portal',
      paymentMethod: 'customerCC',
      pricingTier: 'standard',
      customDiscount: '',
      notes: '',
      createPortalAccount: true,
      portalPassword: '',
      shippingLocations: [{
        name: 'Primary',
        street: '',
        city: '',
        state: '',
        zip: '',
        isDefault: true
      }]
    });
    setShowAddForm(false);
    setEditingCustomer(null);
    setError('');
  };

  const handleEdit = (customer) => {
    setFormData({
      companyName: customer.companyName || '',
      contactName: customer.contactName || '',
      email: customer.email || '',
      phone: customer.phone || '',
      website: customer.website || '',
      repId: customer.repId || '',
      orderMethod: customer.orderMethod || 'portal',
      paymentMethod: customer.paymentMethod || 'customerCC',
      pricingTier: customer.pricingTier || 'standard',
      customDiscount: customer.customDiscount?.toString() || '',
      notes: customer.notes || '',
      createPortalAccount: false,
      portalPassword: '',
      shippingLocations: customer.shippingLocations?.length > 0 
        ? customer.shippingLocations 
        : [{ name: 'Primary', street: '', city: '', state: '', zip: '', isDefault: true }]
    });
    setEditingCustomer(customer.id);
    setShowAddForm(true);
  };

  const addShippingLocation = () => {
    setFormData({
      ...formData,
      shippingLocations: [
        ...formData.shippingLocations,
        { name: '', street: '', city: '', state: '', zip: '', isDefault: false }
      ]
    });
  };

  const removeShippingLocation = (index) => {
    if (formData.shippingLocations.length === 1) return;
    const updated = formData.shippingLocations.filter((_, i) => i !== index);
    // Ensure at least one is default
    if (!updated.some(loc => loc.isDefault)) {
      updated[0].isDefault = true;
    }
    setFormData({ ...formData, shippingLocations: updated });
  };

  const updateShippingLocation = (index, field, value) => {
    const updated = [...formData.shippingLocations];
    if (field === 'isDefault' && value) {
      // Only one can be default
      updated.forEach((loc, i) => loc.isDefault = i === index);
    } else {
      updated[index][field] = value;
    }
    setFormData({ ...formData, shippingLocations: updated });
  };

  const generatePortalLink = (customerId) => {
    return `${window.location.origin}?customer=${customerId}`;
  };

  const copyPortalLink = async (customerId) => {
    const link = generatePortalLink(customerId);
    await navigator.clipboard.writeText(link);
    setCopiedLink(customerId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const isAdminUser = currentUserRole === 'admin';
      const tierDiscount = DEFAULT_PRICING_TIERS[formData.pricingTier]?.discount || 0;

      const customerData = {
        companyName: formData.companyName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        website: formData.website,
        orderMethod: formData.orderMethod,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
        shippingLocations: formData.shippingLocations.filter(loc => loc.street), // Only save locations with addresses
        updatedAt: Timestamp.now()
      };

      if (isAdminUser) {
        // Only admins can assign reps and set pricing/discounts
        customerData.repId = formData.repId || null;
        customerData.pricingTier = formData.pricingTier;
        customerData.customDiscount = formData.customDiscount ? parseFloat(formData.customDiscount) : null;
        customerData.tierDiscount = customerData.customDiscount ?? tierDiscount;
      } else if (!editingCustomer) {
        // Reps: new customers are assigned to themselves at the standard tier
        // discount. On edit, pricing fields are left untouched (rules enforce this).
        customerData.repId = currentUserId;
        customerData.pricingTier = formData.pricingTier;
        customerData.customDiscount = null;
        customerData.tierDiscount = tierDiscount;
      }

      let portalError = null;

      if (editingCustomer) {
        // Update existing customer
        await updateDoc(doc(db, 'customers', editingCustomer), customerData);
      } else {
        // Create new customer
        customerData.createdAt = Timestamp.now();
        customerData.totalOrders = 0;
        customerData.totalSpent = 0;

        const customerRef = await addDoc(collection(db, 'customers'), customerData);

        // Create portal account if requested — done server-side so the current
        // admin/rep session isn't replaced by the new login
        if (formData.createPortalAccount && formData.orderMethod === 'portal' && formData.portalPassword) {
          try {
            const createPortalUser = httpsCallable(functions, 'createCustomerPortalUser');
            await createPortalUser({
              customerId: customerRef.id,
              password: formData.portalPassword
            });
          } catch (authErr) {
            console.error('Error creating portal account:', authErr);
            // Don't fail the whole operation, just note the error
            portalError = `Customer created but portal account failed: ${authErr.message}`;
            setError(portalError);
          }
        }
      }

      if (!portalError) {
        resetForm();
      }
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto" size={32} />
        <p className="text-gray-500 mt-4">Loading customers...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Customer Management</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2"
          >
            <Plus size={18} /> Add Customer
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
          </h3>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Company Info */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 text-gray-700">Company Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Company Name *</label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Contact Name</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Order Settings */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 text-gray-700">Order Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {currentUserRole === 'admin' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Assigned Rep</label>
                    <select
                      value={formData.repId}
                      onChange={(e) => setFormData({ ...formData, repId: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">Direct (No Rep)</option>
                      {reps?.map(rep => (
                        <option key={rep.id} value={rep.id}>{rep.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Order Method</label>
                  <select
                    value={formData.orderMethod}
                    onChange={(e) => setFormData({ ...formData, orderMethod: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {Object.values(ORDER_METHODS).map(method => (
                      <option key={method.key} value={method.key}>{method.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Method</label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {Object.values(PAYMENT_METHODS).map(method => (
                      <option key={method.key} value={method.key}>{method.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Pricing Tier</label>
                  <select
                    value={formData.pricingTier}
                    onChange={(e) => setFormData({ ...formData, pricingTier: e.target.value })}
                    disabled={currentUserRole !== 'admin' && !!editingCustomer}
                    className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    {Object.entries(DEFAULT_PRICING_TIERS).map(([key, tier]) => (
                      <option key={key} value={key}>{tier.name} ({tier.discount}% off)</option>
                    ))}
                  </select>
                  {currentUserRole !== 'admin' && !!editingCustomer && (
                    <p className="text-xs text-gray-500 mt-1">Contact an admin to change pricing</p>
                  )}
                </div>
                {currentUserRole === 'admin' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Custom Discount % (optional)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.customDiscount}
                      onChange={(e) => setFormData({ ...formData, customDiscount: e.target.value })}
                      placeholder="Overrides tier"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Portal Account */}
            {!editingCustomer && formData.orderMethod === 'portal' && (
              <div className="mb-6">
                <h4 className="font-medium mb-3 text-gray-700">Portal Access</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.createPortalAccount}
                        onChange={(e) => setFormData({ ...formData, createPortalAccount: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Create portal login account</span>
                    </label>
                  </div>
                  {formData.createPortalAccount && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Portal Password</label>
                      <input
                        type="password"
                        value={formData.portalPassword}
                        onChange={(e) => setFormData({ ...formData, portalPassword: e.target.value })}
                        minLength={8}
                        placeholder="Min 8 characters"
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Shipping Locations */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-700">Shipping Locations</h4>
                <button
                  type="button"
                  onClick={addShippingLocation}
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <Plus size={14} /> Add Location
                </button>
              </div>
              
              {formData.shippingLocations.map((location, index) => (
                <div key={index} className="border rounded-lg p-4 mb-3">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={location.name}
                        onChange={(e) => updateShippingLocation(index, 'name', e.target.value)}
                        placeholder="Location name (e.g., Warehouse, Store #1)"
                        className="px-3 py-1 border rounded text-sm"
                      />
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          checked={location.isDefault}
                          onChange={() => updateShippingLocation(index, 'isDefault', true)}
                          className="w-4 h-4"
                        />
                        Default
                      </label>
                    </div>
                    {formData.shippingLocations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeShippingLocation(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        value={location.street}
                        onChange={(e) => updateShippingLocation(index, 'street', e.target.value)}
                        placeholder="Street Address"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={location.city}
                        onChange={(e) => updateShippingLocation(index, 'city', e.target.value)}
                        placeholder="City"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={location.state}
                        onChange={(e) => updateShippingLocation(index, 'state', e.target.value)}
                        placeholder="State"
                        maxLength={2}
                        className="w-16 px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={location.zip}
                        onChange={(e) => updateShippingLocation(index, 'zip', e.target.value)}
                        placeholder="ZIP"
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                {editingCustomer ? 'Update Customer' : 'Create Customer'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          {currentUserRole === 'admin' && (
            <select
              value={filterRep}
              onChange={(e) => setFilterRep(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="all">All Reps</option>
              <option value="direct">Direct (No Rep)</option>
              {reps?.map(rep => (
                <option key={rep.id} value={rep.id}>{rep.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Customer List */}
      {filteredCustomers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Building2 size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            {searchTerm || filterRep !== 'all' ? 'No customers found' : 'No customers yet. Add your first customer above.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCustomers.map(customer => {
            const isExpanded = expandedCustomer === customer.id;
            const rep = reps?.find(r => r.id === customer.repId);

            return (
              <div key={customer.id} className="bg-white rounded-lg shadow overflow-hidden">
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedCustomer(isExpanded ? null : customer.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Building2 size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold">{customer.companyName}</p>
                      <p className="text-sm text-gray-500">{customer.contactName} • {customer.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="font-medium">{customer.tierDiscount || 0}% discount</p>
                      <p className="text-gray-500">
                        {rep ? `Rep: ${rep.name}` : 'Direct'}
                      </p>
                    </div>

                    <span className={`px-2 py-1 text-xs rounded-full ${
                      customer.orderMethod === 'portal'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {ORDER_METHODS[customer.orderMethod]?.label.split(' ')[0]}
                    </span>

                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Details */}
                      <div>
                        <h4 className="font-semibold mb-2">Settings</h4>
                        <p className="text-sm"><strong>Order Method:</strong> {ORDER_METHODS[customer.orderMethod]?.label}</p>
                        <p className="text-sm"><strong>Payment:</strong> {PAYMENT_METHODS[customer.paymentMethod]?.label}</p>
                        <p className="text-sm"><strong>Pricing Tier:</strong> {DEFAULT_PRICING_TIERS[customer.pricingTier]?.name}</p>
                        <p className="text-sm"><strong>Discount:</strong> {customer.tierDiscount}%</p>
                        {customer.notes && (
                          <p className="text-sm mt-2"><strong>Notes:</strong> {customer.notes}</p>
                        )}
                      </div>

                      {/* Shipping Locations */}
                      <div>
                        <h4 className="font-semibold mb-2">Shipping Locations</h4>
                        {customer.shippingLocations?.length > 0 ? (
                          <div className="space-y-2">
                            {customer.shippingLocations.map((loc, i) => (
                              <div key={i} className="text-sm p-2 bg-gray-50 rounded">
                                <p className="font-medium">{loc.name} {loc.isDefault && <span className="text-green-600">(Default)</span>}</p>
                                <p className="text-gray-600">{loc.street}</p>
                                <p className="text-gray-600">{loc.city}, {loc.state} {loc.zip}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No locations added</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div>
                        <h4 className="font-semibold mb-2">Actions</h4>
                        <div className="space-y-2">
                          {customer.orderMethod === 'portal' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); copyPortalLink(customer.id); }}
                              className="w-full px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm flex items-center justify-center gap-2"
                            >
                              {copiedLink === customer.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                              {copiedLink === customer.id ? 'Copied!' : 'Copy Portal Link'}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(customer); }}
                            className="w-full px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm flex items-center justify-center gap-2"
                          >
                            <Edit2 size={14} /> Edit Customer
                          </button>
                        </div>

                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm text-gray-500">
                            <strong>Orders:</strong> {customer.totalOrders || 0}
                          </p>
                          <p className="text-sm text-gray-500">
                            <strong>Total Spent:</strong> ${(customer.totalSpent || 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
